import {
  FranchiseType,
  ProductCategoryType,
  Proposition,
  RegionPricing,
} from "@hubcapp/types";
import { productCategoryTypeToCategoryTitle } from "@hubcapp/utils";

import { AreaPrice } from "./types";

export const validateNewPrice = (value: string) =>
  new RegExp(/^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{2})?$/i).test(value);

export const parseAreaPrice = (areaPrice: number) =>
  (Math.round(areaPrice * 100) / 100).toFixed(2);

export const checkAreaPriceValue = (areaPrice: AreaPrice) => {
  if (parseFloat(areaPrice.value) === 0) {
    return {
      error: true,
      errorMessage: "Price must be greater than 0",
    };
  }

  if (parseFloat(areaPrice.value) < areaPrice.minPrice) {
    return {
      error: true,
      errorMessage: `Price cannot be lower than ${areaPrice.minPrice}`,
    };
  }

  if (!validateNewPrice(areaPrice.value)) {
    return {
      error: true,
      errorMessage: "Please enter a valid price",
    };
  }

  if (parseFloat(areaPrice.value) % 0.25 !== 0) {
    return {
      error: true,
      errorMessage:
        "Pence value must be a multiple of 25 eg .00, .25, .50, .75",
    };
  }

  return {
    error: false,
    errorMessage: "",
  };
};

export const validateAreaPricing = (areaPrice: AreaPrice): AreaPrice => ({
  ...areaPrice,
  ...checkAreaPriceValue(areaPrice),
  value: areaPrice.value,
});

export const initialAreaPriceMap = (
  regionPrice: RegionPricing,
  initialValue: number | null,
  id: string,
  type: ProductCategoryType,
): AreaPrice => {
  const recommendedPrice = regionPrice.recommendedPrice ?? 0;

  return {
    id: id || regionPrice.id,
    name: type as string,
    label: `${productCategoryTypeToCategoryTitle(type)} lessons £/hr`,
    value: (initialValue ?? recommendedPrice).toFixed(2),
    helperText: `The recommended regional price for this area is £${recommendedPrice.toFixed(
      2,
    )}/hr`,
    recommendedRate: recommendedPrice,
    useMemo: false,
    error: false,
    errorMessage: "",
    minPrice: regionPrice.minPrice as number,
  };
};

export const getBrandTitle = (
  brand: Proposition["brand"],
  type?: Proposition["type"],
) => {
  if (type && type === FranchiseType.Dit) {
    return "";
  }
  return brand === "AADS" ? "AA" : "BSM";
};

export const getFranchiseTitle = (proposition: Proposition) =>
  `${getBrandTitle(proposition.brand, proposition.type)} ${
    proposition.transmission || ""
  }`.trim();

export const franchiseTypeLabelMap = {
  [FranchiseType.Full]: "Learners",
  [FranchiseType.PartnerLite]: "Learners",
  [FranchiseType.Solo]: "Learners",
  [FranchiseType.Trainee]: "Learners",
  [FranchiseType.ThirdParty]: "Learners",
  [FranchiseType.Dit]: "DIT",
  [FranchiseType.Motability]: "Motability",
};

export const getFranchiseLabel = (
  shouldPrefixProposition = false,
  proposition: Proposition,
): string =>
  `${shouldPrefixProposition ? getFranchiseTitle(proposition) + " " : ""}${
    proposition.type ? franchiseTypeLabelMap[proposition.type] : ""
  }`;
